import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReadinessBadge from '../../components/purchasing/ReadinessBadge';
import PurchaseOrderExcelImportDialog from '../../components/purchasing/PurchaseOrderExcelImportDialog';
import {
  deletePurchaseOrder,
  fetchPurchaseOrders,
  fetchSuppliers,
} from '../../services/purchasingService';
import { PurchaseOrderListItem } from '../../types/purchasing';
import { AuthContext } from '../../context/AuthContext';
import SupplierPicker from '../../components/purchasing/SupplierPicker';
import SeasonPicker from '../../components/purchasing/SeasonPicker';
import {
  readPurchaseOrderListFilters,
  writePurchaseOrderListFilters,
} from '../../utils/urlState';

const statusLabel: Record<string, string> = {
  draft: 'Utkast',
  submitted: 'Skapad',
  transferring: 'Överförs',
  transferred: 'Överförd',
  transfer_failed: 'Överföring misslyckad',
};

const viskanLabel: Record<string, string> = {
  draft: '–',
  submitted: 'Ej överförd',
  transferring: 'Överförs',
  transferred: 'Överförd',
  transfer_failed: 'Fel',
};

const viskanChipColor: Record<
  string,
  'default' | 'success' | 'warning' | 'error' | 'info'
> = {
  draft: 'default',
  submitted: 'default',
  transferring: 'info',
  transferred: 'success',
  transfer_failed: 'error',
};

const canEditPurchaseOrder = (status: PurchaseOrderListItem['status']) =>
  status === 'draft' || status === 'submitted' || status === 'transfer_failed';

const PurchaseOrderList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { permissions } = React.useContext(AuthContext);
  const canDelete = permissions.includes('manage_users');
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<PurchaseOrderListItem[]>([]);
  const [importOpen, setImportOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [suppliers, setSuppliers] = useState<
    Array<{ supplierId: number; supplierName: string }>
  >([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);

  const initialFilters = readPurchaseOrderListFilters(searchParams);
  const [status, setStatus] = useState<string | null>(initialFilters.status);
  const [viskanStatus, setViskanStatus] = useState<string | null>(
    initialFilters.viskanStatus,
  );
  const [ready, setReady] = useState<'all' | 'ready' | 'not_ready'>(
    initialFilters.ready,
  );
  const [season, setSeason] = useState<string | null>(initialFilters.season);
  const [supplierId, setSupplierId] = useState<number | null>(
    initialFilters.supplierId,
  );

  useEffect(() => {
    // keep URL in sync with current filter state
    setSearchParams(
      writePurchaseOrderListFilters(new URLSearchParams(), {
        status,
        viskanStatus,
        ready,
        season,
        supplierId,
      }),
      { replace: true },
    );
  }, [status, viskanStatus, ready, season, supplierId, setSearchParams]);

  const loadSuppliers = async () => {
    setSuppliersLoading(true);
    try {
      const data = await fetchSuppliers();
      setSuppliers(data);
    } catch {
      // non-fatal
    } finally {
      setSuppliersLoading(false);
    }
  };

  const loadOrders = async () => {
    setLoading(true);
    try {
      const response = await fetchPurchaseOrders({
        page: 1,
        limit: 50,
        status: status || undefined,
        viskanStatus: viskanStatus || undefined,
        readyForTransfer:
          ready === 'ready' ? true : ready === 'not_ready' ? false : undefined,
        season: season || undefined,
        supplierId: supplierId || undefined,
      });
      setOrders(response.purchaseOrders);
    } catch (error) {
      console.error('Failed to load purchase orders:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSuppliers();
  }, []);

  useEffect(() => {
    loadOrders();
  }, [status, viskanStatus, ready, season, supplierId]);

  const handleDelete = async (purchaseOrderId: number) => {
    if (!canDelete) {
      setToastMessage(
        'Du saknar behörighet att ta bort inköpsordrar. Kontakta administratör för hjälp.',
      );
      return;
    }
    if (!window.confirm('Ta bort inköpsordern?')) return;
    try {
      await deletePurchaseOrder(purchaseOrderId);
      await loadOrders();
    } catch (error) {
      console.error('Failed to delete purchase order:', error);
      setToastMessage(
        'Kunde inte ta bort inköpsordern. Kontakta administratör om du behöver hjälp.',
      );
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4">Inköpsordrar</Typography>
          <Typography variant="body2" color="text.secondary">
            {orders.length} {orders.length === 1 ? 'order' : 'ordrar'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            onClick={() => navigate('/purchasing/purchase-orders/statistics')}
          >
            Statistik
          </Button>
          <Button variant="outlined" onClick={() => setImportOpen(true)}>
            Importera från Excel
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/purchasing/purchase-orders/new')}
          >
            Ny inköpsorder
          </Button>
        </Stack>
      </Box>

      <Paper
        sx={{
          p: 2,
          mb: 2,
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <TextField
            select
            size="small"
            label="Viskan status"
            value={viskanStatus || ''}
            onChange={(e) => {
              const next = e.target.value || '';
              setViskanStatus(next ? next : null);
              if (next) setStatus(null);
            }}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">
              <em>Alla</em>
            </MenuItem>
            {Object.keys(viskanLabel).map((key) => (
              <MenuItem key={key} value={key}>
                {viskanLabel[key]}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Redo status"
            value={ready}
            onChange={(e) =>
              setReady(e.target.value as 'all' | 'ready' | 'not_ready')
            }
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="all">Alla</MenuItem>
            <MenuItem value="ready">Redo</MenuItem>
            <MenuItem value="not_ready">Ej redo</MenuItem>
          </TextField>

          <TextField
            select
            size="small"
            label="Status"
            value={status || ''}
            onChange={(e) => {
              const next = e.target.value || '';
              setStatus(next ? next : null);
              if (next) setViskanStatus(null);
            }}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">
              <em>Alla</em>
            </MenuItem>
            {Object.keys(statusLabel).map((key) => (
              <MenuItem key={key} value={key}>
                {statusLabel[key]}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ minWidth: 260, flex: 1 }}>
            <SupplierPicker
              suppliers={suppliers}
              value={supplierId}
              onChange={setSupplierId}
              disabled={suppliersLoading}
            />
          </Box>

          <Box sx={{ minWidth: 220 }}>
            <SeasonPicker
              value={season}
              onChange={(code) => setSeason(code || null)}
            />
          </Box>

          <Button
            variant="outlined"
            onClick={() => {
              setStatus(null);
              setViskanStatus(null);
              setReady('all');
              setSeason(null);
              setSupplierId(null);
            }}
          >
            Rensa filter
          </Button>
        </Stack>
      </Paper>

      <PurchaseOrderExcelImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onCreated={(order) =>
          navigate(`/purchasing/purchase-orders/edit/${order.purchaseOrderId}`)
        }
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : orders.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6">Inga inköpsordrar än</Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1, mb: 3 }}
          >
            Skapa din första inköpsorder för att komma igång.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/purchasing/purchase-orders/new')}
          >
            Ny inköpsorder
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ordernr</TableCell>
                <TableCell>Lev. ordernr</TableCell>
                <TableCell>Leverantör</TableCell>
                <TableCell>Säsong</TableCell>
                <TableCell>Leverans</TableCell>
                <TableCell align="right">Antal</TableCell>
                <TableCell align="right">Inköpsvärde</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Viskan</TableCell>
                <TableCell>Redo</TableCell>
                <TableCell>Åtgärder</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.purchaseOrderId} hover>
                  <TableCell>{order.orderNumber}</TableCell>
                  <TableCell>
                    {order.supplierOrderNumber?.trim() || '–'}
                  </TableCell>
                  <TableCell>{order.supplierName || '-'}</TableCell>
                  <TableCell>{order.season || '-'}</TableCell>
                  <TableCell>{order.defaultDeliveryMonth}</TableCell>
                  <TableCell align="right">{order.totalUnits}</TableCell>
                  <TableCell align="right">
                    {new Intl.NumberFormat('sv-SE', {
                      style: 'currency',
                      currency: order.currency,
                      maximumFractionDigits: 0,
                    }).format(order.totalCost || 0)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={statusLabel[order.status] || order.status}
                      color={order.status === 'draft' ? 'warning' : 'success'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={viskanLabel[order.status] || '–'}
                      color={viskanChipColor[order.status] || 'default'}
                      variant={
                        order.status === 'transferred' ? 'filled' : 'outlined'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <ReadinessBadge
                      ready={order.readyForTransfer}
                      missingFields={order.missingFieldsSummary}
                    />
                  </TableCell>
                  <TableCell>
                    {canEditPurchaseOrder(order.status) ? (
                      <>
                        <IconButton
                          size="small"
                          title={
                            order.status === 'draft'
                              ? 'Redigera order'
                              : 'Öppna order för komplettering'
                          }
                          onClick={() =>
                            navigate(
                              `/purchasing/purchase-orders/edit/${order.purchaseOrderId}`,
                            )
                          }
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        {order.status === 'draft' && canDelete ? (
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(order.purchaseOrderId)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        ) : null}
                      </>
                    ) : (
                      <IconButton
                        size="small"
                        title="Visa överförd order"
                        onClick={() =>
                          navigate(
                            `/purchasing/purchase-orders/view/${order.purchaseOrderId}`,
                          )
                        }
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={Boolean(toastMessage)}
        autoHideDuration={4000}
        onClose={() => setToastMessage(null)}
        message={toastMessage}
      />
    </Box>
  );
};

export default PurchaseOrderList;
